import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { ShieldWarning, Clock, Warning, CheckCircle, ArrowSquareOut, Scales } from "@phosphor-icons/react";

/**
 * LegalRadar — SLA dashboard for every WO carrying a legal-exposure tag
 * (Fair Housing / Moisture / Mold / Habitability / ADA / Water Intrusion).
 *
 * Route: /admin/radar/legal
 *
 * Traffic light per row based on hours since last touch:
 *   green  < 4h    (fresh)
 *   amber  4-24h   (needs attention)
 *   red    > 24h   (SLA breached, escalation eligible)
 *
 * This is the surface that stops sensitive complaints from getting lost
 * in a chat channel. Every legal WO shows up here — no manual triage.
 */
export default function LegalRadar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    api.get("/legal-radar")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load radar"));
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (err) return <AdminShell><div className="p-8 font-bold text-red-700" data-testid="legal-radar-err">{err}</div></AdminShell>;
  if (!data) return <AdminShell><div className="p-8 font-bold">Loading legal radar…</div></AdminShell>;

  const items = data.items || [];
  const counts = data.counts || {};

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="legal-radar-page">
        <div className="flex items-center gap-3">
          <Scales weight="fill" size={32} className="text-red-700"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Legal Radar</h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Every open work order carrying a legal-exposure tag · auto-escalated when SLA breaches
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Kpi label="Red · SLA breached" value={counts.red || 0} tone="red" testid="legal-kpi-red"/>
          <Kpi label="Amber · needs attention" value={counts.amber || 0} tone="amber" testid="legal-kpi-amber"/>
          <Kpi label="Green · fresh" value={counts.green || 0} tone="green" testid="legal-kpi-green"/>
        </div>

        <div className="mt-6 rugged-card bg-white overflow-hidden">
          <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center gap-2 bg-slate-50">
            <ShieldWarning weight="bold" size={16} className="text-slate-700"/>
            <div className="font-display font-black text-sm uppercase tracking-wider">Open Legal WOs</div>
            <span className="text-xs text-slate-500 font-bold ml-auto">{items.length} total · auto-refreshes every 60s</span>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center" data-testid="legal-radar-empty">
              <CheckCircle weight="fill" size={44} className="mx-auto text-emerald-500"/>
              <div className="mt-2 font-black uppercase text-sm tracking-widest text-slate-600">All clear</div>
              <div className="text-xs text-slate-500 mt-1">No open work orders carry a legal-exposure tag right now.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {items.map(it => <LegalRow key={it.job_id} it={it}/>)}
            </div>
          )}
        </div>
        <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
          Escalation ladder — 4h no touch → office · 24h → founder · 48h → all admins
        </div>
      </div>
    </AdminShell>
  );
}

function Kpi({ label, value, tone, testid }) {
  const tones = {
    red: "border-red-500 bg-red-50 text-red-700",
    amber: "border-amber-500 bg-amber-50 text-amber-700",
    green: "border-emerald-500 bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`rugged-card p-4 border-l-8 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display font-black text-4xl mt-1">{value}</div>
    </div>
  );
}

function LegalRow({ it }) {
  const bucketCls = it.bucket === "red" ? "border-l-8 border-red-500 bg-red-50"
    : it.bucket === "amber" ? "border-l-8 border-amber-500 bg-amber-50"
    : "border-l-8 border-emerald-500 bg-white";
  const chipCls = it.bucket === "red" ? "bg-red-600 text-white"
    : it.bucket === "amber" ? "bg-amber-500 text-white"
    : "bg-emerald-600 text-white";
  return (
    <div className={`p-4 ${bucketCls}`} data-testid={`legal-row-${it.job_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${chipCls}`}>
              {it.category_label}
            </span>
            {it.escalated_stage && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-fuchsia-600 text-white inline-flex items-center gap-0.5">
                <Warning weight="bold" size={10}/> Escalated · {it.escalated_stage}
              </span>
            )}
            {!it.acknowledged && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-500 text-white">
                Not acknowledged
              </span>
            )}
            {it.satisfied && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white inline-flex items-center gap-0.5">
                <CheckCircle weight="bold" size={10}/> Evidence complete
              </span>
            )}
          </div>
          <div className="mt-1 font-display font-black text-base leading-tight">
            {it.work_order && <span className="text-slate-500 mr-2">{it.work_order}</span>}
            {it.title}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {it.property_address}{it.unit ? ` · Unit ${it.unit}` : ""} · {it.assigned_to_name || "unassigned"}
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1 inline-flex items-center gap-2 flex-wrap">
            <Clock weight="bold" size={11}/>
            <span>{Math.round(it.hours_since_touch)}h since touch</span>
            <span>· {Math.round(it.hours_since_created)}h since created</span>
            <span>· photos {it.photos_total}/{it.photos_needed}</span>
            <span>· checklist {it.required_done}/{it.required_total}</span>
          </div>
          {it.missing && it.missing.length > 0 && (
            <div className="mt-1 text-[11px] font-mono text-red-700">
              missing: {it.missing.join(" · ")}
            </div>
          )}
        </div>
        <Link to={`/jobs/${it.job_id}`} data-testid={`legal-open-${it.job_id}`}
          className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-white inline-flex items-center gap-1 self-start">
          <ArrowSquareOut weight="bold" size={11}/> Open WO
        </Link>
      </div>
    </div>
  );
}
